/**
 * CourseModuleRegistry — core types (F2-A, Design A).
 *
 * Space Academy's Moodle spine: within a `unit` live `course_module`s of
 * pluggable kinds (lesson | resource | assignment | forum | video |
 * live_session | …). Just like `learning-blocks`, the CORE never knows the
 * concrete kinds: it iterates modules and delegates to the registered
 * plugin. Adding a kind = registering a plugin (F3 adds assignment/forum/video/live_session
 * without touching the core — invariant proven in tests).
 *
 * UI components (authoring/player) are GENERIC (`TComponent`) so this
 * package stays framework-agnostic: the web app instantiates the registry
 * with its lazy Solid components.
 */

import type { FeatureManifest } from "../country/packs";

/** Result of validating a module's config/settings (mirror of learning-blocks). */
export interface ModuleValidationOk<T> {
  ok: true;
  value: T;
}
export interface ModuleValidationErr {
  ok: false;
  errors: string[];
}
export type ModuleValidationResult<T> = ModuleValidationOk<T> | ModuleValidationErr;

/**
 * Typed validator (no zod in deps): `parse` validates and NORMALIZES. The
 * shape is compatible with a future swap to zod (`schema.safeParse` adapts trivially).
 */
export interface SchemaValidator<T> {
  parse(value: unknown): ModuleValidationResult<T>;
}

/** Generic `course_module` row as it arrives from the local-first client. */
export interface CourseModuleRow {
  id: string;
  unit_id: string | null;
  course_id: string | null;
  kind: string | null;
  ref_id: string | null;
  position: number | null;
  visibility: string | null;
  completion_rule: string | null;
  config: string | null;
}

/** Context for computing a module's completion contribution (0..1). */
export interface ModuleCompletionCtx {
  module: CourseModuleRow;
  /** Student's lesson progress for ref_id (if it applies to the kind). */
  lessonProgress?: { status: string | null; score: number | null } | null;
}

/** Context for computing a module's grade contribution (0..1). */
export interface ModuleGradeCtx {
  module: CourseModuleRow;
  lessonProgress?: { status: string | null; score: number | null } | null;
}

/**
 * A pluggable module kind.
 *
 * `manifest.key` follows the self-describing catalog convention:
 * `academy.module.<kind>` — the manifest feeds `pnpm gen:features` (F2-D)
 * and from there the Rust `FeatureCatalog` (no hidden feature).
 */
export interface CourseModulePlugin<
  TComponent = unknown,
  Config = Record<string, unknown>,
  Settings = Record<string, unknown>,
> {
  /** Stable identifier of the kind (e.g. "lesson", "resource"). */
  readonly kind: string;
  /** Self-describing metadata (feature catalog). */
  readonly manifest: FeatureManifest;
  /** Validates and normalizes `course_module.config`. */
  readonly configSchema: SchemaValidator<Config>;
  /** Validates per-cohort settings (cohort_module_setting.config). */
  readonly settingsSchema?: SchemaValidator<Settings>;
  /** LAZY loader for the module's authoring editor (injected by the web app). */
  readonly authoringComponent?: () => Promise<TComponent>;
  /** LAZY loader for the module's consumption component (injected by the web app). */
  readonly playerComponent?: () => Promise<TComponent>;
  /**
   * Module completion contribution (0..1) or `null` if the kind doesn't
   * contribute to the course's completion criterion (e.g. a resource).
   */
  completionContribution?(ctx: ModuleCompletionCtx): number | null;
  /** Grade contribution (0..1) or `null` if the kind doesn't score. */
  gradeContribution?(ctx: ModuleGradeCtx): number | null;
}

/** Type guard: is it a plain object (Record)? No unsafe assertions. */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Permissive validator helper: accepts any object (kinds with no config). */
export function anyObjectValidator(): SchemaValidator<Record<string, unknown>> {
  return {
    parse(value: unknown) {
      if (value === null || value === undefined) {
        return { ok: true, value: {} };
      }
      if (isPlainObject(value)) {
        return { ok: true, value };
      }
      return { ok: false, errors: ["config must be an object"] };
    },
  };
}
